import React from 'react';
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import { MapPin, Briefcase, Laptop, Heart, DollarSign, Coffee, ArrowRight } from "lucide-react";

const Careers = () => {
  return (
    <div className="pt-32 pb-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header Stream */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            <span className="text-sm text-slate-400">We are actively hiring</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Help us build the <br />
            <span className="gradient-text">Future of Payments</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Join a globally distributed team of passionate problem-solvers. We're on a mission to reshape financial infrastructure, and we need your expertise.
          </p>
        </motion.div>

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-24"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Perks & Benefits</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              We invest heavily in our team. Here's what you can expect when you join Luxkgate.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Laptop,
                title: "Work from Anywhere",
                description: "We are a remote-first team. Work from the comfort of your home, anywhere in the world."
              },
              {
                icon: DollarSign,
                title: "Competitive Pay",
                description: "We offer top-tier compensation packages and equity options for all full-time employees."
              },
              {
                icon: Heart,
                title: "Health & Wellness",
                description: "Comprehensive health, dental, and vision coverage to keep you and your family healthy."
              },
              {
                icon: Coffee,
                title: "Flexible Hours",
                description: "We care about the work you do, not when you do it. Enjoy flexible schedules and unlimited PTO."
              }
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.1 }}
              >
                <Card className="bg-slate-900/50 border-slate-800/50 h-full hover:border-slate-700 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center mb-4">
                      <benefit.icon className="h-5 w-5 text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Open Positions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between border-b border-slate-800 pb-4 mb-8">
            <h2 className="text-3xl font-bold text-white">Open Roles</h2>
            <div className="text-sm text-slate-400">3 roles available</div>
          </div>

          <div className="space-y-4">
            {[
              {
                title: "Senior Full-Stack Engineer",
                department: "Engineering",
                location: "Remote, Global",
                type: "Full-Time"
              },
              {
                title: "DevOps / Infrastructure Specialist",
                department: "Platform",
                location: "Remote, Global",
                type: "Full-Time"
              },
              {
                title: "Technical Account Manager",
                department: "Customer Success",
                location: "North America",
                type: "Full-Time"
              }
            ].map((job, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.1 }}
                className="group p-6 rounded-2xl bg-slate-900/50 border border-slate-800/50 hover:border-blue-500/50 transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">{job.title}</h3>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
                      <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4" /> {job.department}</span>
                      <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {job.location}</span>
                      <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700">{job.type}</span>
                    </div>
                  </div>
                  <Button className="shrink-0 bg-slate-800 hover:bg-slate-700 text-white w-full md:w-auto">
                    Apply Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Careers;
